import React, { useMemo, useRef } from "react";

import ReactMarkdown, { type Components } from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import type { PluggableList } from "unified";

import { splitMarkdownBlocks } from "./markdown-blocks";
import {
  modelOutputSanitizeSchema,
  rehypeAnnotationProcessor,
  rehypeStreamingFade,
  rehypeStreamingMarker,
  type AnnotationCount,
} from "./rehype-plugins";

interface MarkdownBlocksProps {
  text: string;
  components: Components;
  remarkPlugins: PluggableList;
  /** The run is over: nothing is still open. */
  hasFinished: boolean;
  /** New words fade in (`rehypeStreamingFade`). */
  isFadingIn: boolean;
}

/**
 * Markdown rendered a block at a time, so a streaming answer re-parses only the block being written rather
 * than the whole document every frame, which on a long answer is what would drop frames. Finished blocks are
 * memoised; annotation numbering is carried from block to block so ids match a single render. Blocks are
 * joined by the newline a single render puts between top-level elements (paragraphs render inline).
 */
export function MarkdownBlocks({ text, components, remarkPlugins, hasFinished, isFadingIn }: MarkdownBlocksProps) {
  const blocks = useMemo(() => splitMarkdownBlocks(text), [text]);
  const ends = useRef<AnnotationCount[]>([]).current;
  return blocks.map((block, index) => (
    <React.Fragment key={index}>
      {index > 0 && "\n"}
      <MarkdownBlock
        components={components}
        ends={ends}
        // Only the last block can hold a tag that is still open; the others are finished whatever the run
        // is doing, so the run ending does not re-render them.
        hasFinished={hasFinished || index < blocks.length - 1}
        index={index}
        isFadingIn={isFadingIn}
        remarkPlugins={remarkPlugins}
        text={block}
      />
    </React.Fragment>
  ));
}

interface MarkdownBlockProps extends Omit<MarkdownBlocksProps, "text"> {
  text: string;
  index: number;
  ends: AnnotationCount[];
}

const MarkdownBlock = React.memo(function MarkdownBlock({
  text,
  index,
  ends,
  hasFinished,
  isFadingIn,
  components,
  remarkPlugins,
}: MarkdownBlockProps) {
  const rehypePlugins: PluggableList = useMemo(
    () => [
      rehypeRaw,
      // Straight after the raw HTML is parsed, before any plugin adds properties of its own: the model's
      // markup is untrusted, and without this an `<iframe srcdoc>` in an answer runs script on the page.
      [rehypeSanitize, modelOutputSanitizeSchema],
      rehypeHighlight,
      [rehypeStreamingMarker, { hasFinished }],
      [rehypeAnnotationProcessor, { chain: { ends, index } }],
      // Last, so every tag above has already been given its component and only prose is wrapped.
      [rehypeStreamingFade, { hasFinished: !isFadingIn }],
    ],
    [ends, index, hasFinished, isFadingIn],
  );
  return (
    <ReactMarkdown components={components} rehypePlugins={rehypePlugins} remarkPlugins={remarkPlugins}>
      {text}
    </ReactMarkdown>
  );
});
